import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { toast } from 'sonner';
import RoutineDisplay from '@/components/training/RoutineDisplay';
import { generateRoutine } from '@/components/training/routineGenerator';
import { TrainingProvider, useTraining } from '@/components/training/TrainingContext';
import TrainingForm from '@/components/training/TrainingForm';

function HomeContent() {
  const { generatedRoutine, setGeneratedRoutine, saveRoutine, clearCurrentRoutine } = useTraining();
  const [_formData, _setFormData] = useState<unknown | null>(null);

  const handleRegenerate = () => {
    if (generatedRoutine) {
      const newRoutine = generateRoutine({
        sport: generatedRoutine.sport,
        days: generatedRoutine.daysPerWeek,
        level: generatedRoutine.level,
        equipment: generatedRoutine.equipment,
      });
      setGeneratedRoutine(newRoutine);
      toast.success('New routine generated!');
    }
  };

  const handleSave = () => {
    if (generatedRoutine) {
      saveRoutine(generatedRoutine);
      toast.success('Routine saved successfully!');
    }
  };

  const handleBack = () => {
    clearCurrentRoutine();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50">
      {/* Background decorations */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-violet-200/30 to-indigo-200/30 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-orange-200/30 to-amber-200/30 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-gradient-to-br from-cyan-200/20 to-teal-200/20 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8 sm:py-12 max-w-5xl">
        {/* Logo/Brand */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm shadow-sm border border-slate-100">
            <span className="text-2xl">🏋️</span>
            <span className="font-bold text-slate-700">FitRoutine</span>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          {!generatedRoutine ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
            >
              <TrainingForm />
            </motion.div>
          ) : (
            <motion.div
              key="routine"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
            >
              <RoutineDisplay
                routine={generatedRoutine}
                onRegenerate={handleRegenerate}
                onSave={handleSave}
                onBack={handleBack}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-12 text-sm text-slate-400"
        >
          <p>Train smart. Train consistently. 💪</p>
        </motion.footer>
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <TrainingProvider>
      <HomeContent />
    </TrainingProvider>
  );
}
